import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useAuthStore } from '@/stores/authStore'
import { useUIStore } from '@/stores/uiStore'
import { useClickOutside } from '@/hooks/useClickOutside'
import { LocalAuthForm } from './LocalAuthForm'
import { signInWithGooglePopup } from './GoogleSignInPopup'
import { X } from 'lucide-react'

export function AuthModal() {
  const navigate = useNavigate()
  const { user, fetchUser } = useAuthStore()
  const { authModalOpen, authModalReturnTo, closeAuthModal } = useUIStore()
  const [googleError, setGoogleError] = useState<string | null>(null)
  const panelRef = useRef<HTMLDivElement>(null)

  useClickOutside(panelRef, closeAuthModal, authModalOpen)

  // Close modal automatically once the user signs in.
  useEffect(() => {
    if (authModalOpen && user) {
      closeAuthModal()
      navigate(authModalReturnTo ?? '/account/dashboard')
    }
  }, [authModalOpen, user, authModalReturnTo, closeAuthModal, navigate])

  async function handleGoogleSignIn() {
    setGoogleError(null)
    try {
      const result = await signInWithGooglePopup()
      if (!result.success) {
        throw new Error(result.error || 'google_sign_in_failed')
      }
      await fetchUser()
      closeAuthModal()
      navigate(authModalReturnTo ?? '/account/dashboard')
    } catch (err) {
      const code = err instanceof Error ? err.message : 'unknown_error'
      if (code === 'popup_blocked') {
        setGoogleError('Please allow pop-ups and try again.')
      } else if (code === 'popup_closed') {
        setGoogleError('Sign-in was cancelled.')
      } else if (code === 'popup_timeout') {
        setGoogleError('Sign-in timed out. Please try again.')
      } else {
        setGoogleError('Could not complete Google sign-in. Please try again.')
      }
    }
  }

  // Close on escape.
  useEffect(() => {
    if (!authModalOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeAuthModal()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [authModalOpen, closeAuthModal])

  return (
    <AnimatePresence>
      {authModalOpen && (
        <>
          <motion.div
            initial={{ opacity: 0, backdropFilter: 'blur(0px)' }}
            animate={{ opacity: 1, backdropFilter: 'blur(8px)' }}
            exit={{ opacity: 0, backdropFilter: 'blur(0px)' }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="fixed inset-0 z-[80] bg-ink/40"
            onClick={closeAuthModal}
            style={{ willChange: 'opacity, backdrop-filter' }}
          />
          <div className="fixed inset-0 z-[90] flex items-start justify-center overflow-y-auto p-4 pt-[max(16px,5vh)]">
            <motion.div
              ref={panelRef}
              initial={{ opacity: 0, y: 24, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 24, scale: 0.98 }}
              transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
              className="relative w-full max-w-md bg-paper p-6 shadow-2xl"
            >
            <button
              onClick={closeAuthModal}
              className="absolute right-4 top-4 text-carbon/50 hover:text-wd-orange"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>

            <h2 className="mb-2 font-display text-3xl normal-case text-carbon">
              Welcome to little sky
            </h2>
            <p className="mb-6 text-sm text-carbon/60">
              Sign in to save your wishlist, track orders, and earn loyalty rewards.
            </p>

            {googleError && (
              <div className="mb-4 border border-line bg-card p-3 text-sm text-carbon">
                {googleError}
              </div>
            )}

            <button
              type="button"
              onClick={handleGoogleSignIn}
              className="mb-6 flex w-full items-center justify-center gap-3 border border-ink/10 bg-white px-6 py-3 text-sm text-carbon transition-colors hover:border-ink/30"
            >
              <svg className="h-5 w-5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
              Continue with Google
            </button>

            <div className="mb-6 flex items-center gap-4">
              <div className="h-px flex-1 bg-ink/10" />
              <span className="text-xs uppercase tracking-[0.15em] text-carbon/50">or</span>
              <div className="h-px flex-1 bg-ink/10" />
            </div>

            <LocalAuthForm
              onSuccess={() => {
                fetchUser()
              }}
            />

            <p className="mt-6 text-center text-xs text-carbon/50">
              By continuing, you agree to our{' '}
              <a href="/terms" className="underline hover:text-wd-orange" onClick={closeAuthModal}>
                Terms
              </a>{' '}
              and{' '}
              <a href="/privacy" className="underline hover:text-wd-orange" onClick={closeAuthModal}>
                Privacy Policy
              </a>
              .
            </p>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  )
}
